import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { EMAIL, NAME, PHONE, TRIGGER_NOTIFICATION, JOB_ROLE } from '../../../../../utils/constants/companyLogin';
import { CountryCodeData } from '../../../../Company/CompanyLogin/CountryCodesDropdown/CountryCodesDropdown';
import { DropdownOption } from '../../../../../common/_custom/Dropdown/dropdownTypes';
import { DEFAULT_JOB_ROLES } from './AddUserModal';
import { editUserDetailsV2 } from '../../../../../store/investor/apiHelpers';
import { toast } from 'react-toastify';
import { setEditUserModal } from '../../../../../store/investor/action';
import { emailRegex, extractPhoneNumber } from '../../../../../utils/utils';
import { Mixpanel } from '../../../../../utils/mixpanel';

interface UseEditUserModalProps {
    isOpen: boolean;
    userData: any;
    allRowsData?: any;
    onUsersUpdate?: (updatedUsers: any[]) => void;
    countryCodes: CountryCodeData[];
    onClose: () => void;
}

export const useEditUserModal = ({ isOpen, userData, allRowsData, onUsersUpdate, countryCodes, onClose }: UseEditUserModalProps) => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [selectedOrgData, setSelectedOrgData] = useState<any>(allRowsData || []);

    const [formData, setFormData] = useState<any>({
        [NAME]: '',
        [EMAIL]: '',
        [PHONE]: '',
        [JOB_ROLE]: null,
        [TRIGGER_NOTIFICATION]: false,
    });

    const [formErrors, setFormErrors] = useState({
        [NAME]: '',
        [EMAIL]: '',
        [PHONE]: '',
    });

    // Initialize form data when modal opens
    useEffect(() => {
        if (!isOpen || !userData || !countryCodes?.length) {
            return;
        }

        // Extract phone number from mobileNumber
        const mobileNumber = userData.mobileNumber || '';
        const { phoneNumber } = extractPhoneNumber(mobileNumber, userData.countryCode, countryCodes);

        // Extract job role
        let jobRole: DropdownOption | null = null;
        const jobRoleLabel = userData.customFields?.jobRole;
        if (jobRoleLabel) {
            jobRole =
                DEFAULT_JOB_ROLES.find((role) => role.label === jobRoleLabel) ??
                ({ id: jobRoleLabel, label: jobRoleLabel } as DropdownOption);
        }

        setFormData({
            [NAME]: userData.name || '',
            [EMAIL]: userData.emailId || '',
            [PHONE]: phoneNumber,
            [JOB_ROLE]: jobRole,
            [TRIGGER_NOTIFICATION]: false,
        });
    }, [isOpen, userData, countryCodes]);

    // Update selectedOrgData when allRowsData changes
    useEffect(() => {
        if (!Array.isArray(allRowsData)) {
            return;
        }
        setSelectedOrgData(allRowsData);
    }, [allRowsData]);

    // Update selectedOrgData when formData changes
    // We need to keep the drawer state updated when the user updates some information.
    // This is done using a callback function which sets this state in the parent component when user saves the changes.
    useEffect(() => {
        if (!userData?.userId) {
            return;
        }

        setSelectedOrgData((prevData: any[]) => {
            // Early return if prevData is not a valid array
            if (!Array.isArray(prevData) || prevData.length === 0) {
                return prevData;
            }

            const userIndex = prevData.findIndex((user: any) => user.userId === userData.userId);
            if (userIndex === -1) {
                return prevData;
            }

            // Find the country code information
            const countryCodeInfo = countryCodes?.find((cc) => cc.countryCode === userData.countryCode);

            // Reconstruct mobileNumber with country code
            const phoneNumber = formData[PHONE] || '';
            const mobileNumber = phoneNumber && countryCodeInfo ? `+${countryCodeInfo.callingCode} ${phoneNumber}` : phoneNumber || '';

            const isEmailChanged = formData[EMAIL]?.trim() !== allRowsData[userIndex].emailId?.trim();

            const updatedData = [...prevData];
            updatedData[userIndex] = {
                ...updatedData[userIndex],
                name: formData[NAME] || updatedData[userIndex].name,
                emailId: formData[EMAIL] || updatedData[userIndex].emailId,
                mobileNumber: mobileNumber,
                userStatus: isEmailChanged ? 'Unverified' : allRowsData[userIndex].userStatus,
                customFields: {
                    ...updatedData[userIndex].customFields,
                    jobRole: formData[JOB_ROLE]?.label ?? updatedData[userIndex].customFields?.jobRole ?? '',
                },
            };
            return updatedData;
        });
    }, [formData, userData?.userId, countryCodes]);

    const handleFormChange = (val: any, identifier: string) => {
        setFormData((data) => ({
            ...data,
            [identifier]: val,
        }));

        // Clear error for the changed field
        if (formErrors[identifier]) {
            setFormErrors((errors) => ({
                ...errors,
                [identifier]: '',
            }));
        }
    };

    const validateForm = (): boolean => {
        const errors = { [NAME]: '', [EMAIL]: '', [PHONE]: '' };
        let hasErrors = false;

        // Validate name
        if (!formData[NAME]?.trim()) {
            errors[NAME] = 'Name is required';
            hasErrors = true;
        }

        // Validate email
        const email = formData[EMAIL]?.trim();
        if (!email) {
            errors[EMAIL] = 'Email is required';
            hasErrors = true;
        } else if (!emailRegex.test(email)) {
            errors[EMAIL] = 'Enter a valid email address';
            hasErrors = true;
        }

        // Validate phone (optional, but if provided must be valid)
        const phone = formData[PHONE]?.trim();
        if (phone && phone.length < 10) {
            errors[PHONE] = 'Enter a valid phone number';
            hasErrors = true;
        }

        setFormErrors(errors);
        return !hasErrors;
    };

    const handleSave = async (): Promise<void> => {
        if (!validateForm() || !userData?.userId) {
            return;
        }

        setLoading(true);
        try {
            const payload = {
                userDetails: {
                    userId: userData.userId,
                    name: formData[NAME]?.trim() || '',
                    emailId: formData[EMAIL]?.trim() || '',
                    phoneNumber: formData[PHONE]?.trim() || '',
                    customFields: {
                        jobRole: formData[JOB_ROLE]?.label || '',
                    },
                },
                triggerNotification: formData[TRIGGER_NOTIFICATION] ?? false,
            };

            const response = await editUserDetailsV2(payload);
            if (response?.responseData?.responseCode === 20) {
                toast.success('User updated successfully');
                onUsersUpdate?.(selectedOrgData);
                dispatch(setEditUserModal(false, null, true));
                Mixpanel.track('edited_user_type', {
                    user_email: formData[EMAIL],
                    current_url: window.location.href,
                });
                resetForm();
            }
        } catch (error) {
            console.error('Failed to edit user:', error);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            [NAME]: '',
            [EMAIL]: '',
            [PHONE]: '',
            [JOB_ROLE]: null,
            [TRIGGER_NOTIFICATION]: false,
        });
        setFormErrors({
            [NAME]: '',
            [EMAIL]: '',
            [PHONE]: '',
        });
        setSelectedOrgData([]);
    };

    const onClosedModal = () => {
        resetForm();
        onClose();
    };

    const isSaveDisabled = !formData[NAME]?.trim() || !formData[EMAIL]?.trim() || !emailRegex.test(formData[EMAIL] || '') || loading;

    return {
        loading,
        formData,
        formErrors,
        selectedOrgData,
        handleFormChange,
        handleSave,
        onClosedModal,
        isSaveDisabled,
    };
};
